/**
 * DisclaimerModal
 *
 * Exact text from FOM index.html disclaimer-modal div.
 * Must be accepted every session (sessionStorage, not localStorage).
 * Fires disclaimerLog to Worker on accept — fire and forget.
 */
import { workerPostV1 } from '../../api/worker'
import { type FomSession } from '../../stores/auth'

interface Props {
  session: FomSession | null
  onAccept: () => void
  onDecline: () => void
}

export default function DisclaimerModal({ session, onAccept, onDecline }: Props) {

  async function handleAccept() {
    // Fire-and-forget disclaimer log — mirrors FOM logDisclaimer()
    if (session) {
      try {
        await workerPostV1('/api/v1/disclaimer-log', {
          name: session.name,
          callSign: session.callSign,
          employeeNo: session.employeeNo,
          logAction: 'login',
          device: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
          timestamp: new Date().toISOString(),
        })
      } catch { /* silent — non-blocking */ }
    }
    sessionStorage.setItem('fom_disclaimer_accepted', '1')
    onAccept()
  }

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.85)',
      zIndex: 99999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        background: 'var(--bg-deep)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        maxWidth: 560,
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: '32px 28px',
        color: 'var(--text-primary)',
        fontFamily: 'Arial, sans-serif',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🛡️</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent)', letterSpacing: 0.5 }}>
            FOM Security Inventory Management
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#ef4444', marginTop: 8, letterSpacing: 1, textTransform: 'uppercase' }}>
            Disclaimer
          </div>
        </div>

        {/* Body — exact text from source */}
        <div style={{ fontSize: 13, lineHeight: 1.7, color: '#cbd5e1' }}>
          <p style={{ marginBottom: 14 }}>
            This application has not been endorsed, approved, or authorized by Allied Universal or Olshan Properties.
            It is an independently developed tool created to support daily security operations at Fair Oaks Mall.
          </p>
          <p style={{ marginBottom: 10, fontWeight: 700, color: '#e2e8f0' }}>
            By continuing to use this application, you acknowledge and agree to the following:
          </p>
          <ul style={{ paddingLeft: 20, marginBottom: 14, listStyle: 'disc' }}>
            <li style={{ marginBottom: 8 }}>
              This app is provided "as-is" for operational convenience and is not an official company system.
              It does not replace any existing company-mandated tools, procedures, or reporting requirements.
            </li>
            <li style={{ marginBottom: 8 }}>
              All registered users are participating voluntarily to evaluate, test, and improve the application.
              Your feedback is encouraged and valued.
            </li>
            <li style={{ marginBottom: 8 }}>
              No guarantees are made regarding the availability, accuracy, or reliability of this application.
              The developer assumes no liability for any errors, data loss, or operational disruptions that may arise from its use.
            </li>
            <li style={{ marginBottom: 8 }}>
              Data entered into this app is used solely for shift management purposes and is not shared with any third party.
              However, this app is not a certified secure platform and should not be used to store sensitive personal information
              beyond what is required for shift operations.
            </li>
            <li style={{ marginBottom: 8 }}>
              This application may be updated, modified, or discontinued at any time without prior notice.
            </li>
            <li style={{ marginBottom: 8 }}>
              Use of this application is entirely at your own discretion and risk. It is your responsibility to ensure
              that your use complies with all applicable company policies and guidelines.
            </li>
          </ul>
          <p style={{ fontWeight: 700, color: '#e2e8f0', marginBottom: 16 }}>
            Your continued use constitutes acceptance of these terms.
          </p>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={onDecline} style={{
            flex: 1, padding: 12,
            background: '#374151', color: '#9ca3af',
            border: 'none', borderRadius: 10,
            fontSize: 14, fontWeight: 700, cursor: 'pointer',
          }}>
            Decline
          </button>
          <button onClick={handleAccept} style={{
            flex: 1, padding: 12,
            background: 'var(--accent)', color: 'var(--bg-deep)',
            border: 'none', borderRadius: 10,
            fontSize: 14, fontWeight: 700, cursor: 'pointer',
          }}>
            I Acknowledge &amp; Accept
          </button>
        </div>
      </div>
    </div>
  )
}
